import { useEffect, useState } from "react";
import Modal from "react-modal";
import Select, {
  type CSSObjectWithLabel,
  type SingleValue,
} from "react-select";
import {
  ExtractionType,
  type FieldExtractorEntity,
  FieldMapping,
  useShallowContactsQuery,
} from "../../../generated/graphql";
import { enumKeys } from "../../../utils/enum";

type EditFieldExtractorModalProps = {
  readonly companyUuid: string | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly fieldExtractor: FieldExtractorEntity | undefined;
  readonly handleSave: ({
    type,
    value,
    mapping,
  }: {
    type: ExtractionType;
    value: string;
    mapping: FieldMapping;
  }) => void;
};

const EditFieldExtractorModal = ({
  companyUuid,
  isOpen,
  onClose,
  handleSave,
  fieldExtractor,
}: EditFieldExtractorModalProps) => {
  const { data } = useShallowContactsQuery({
    variables: { companyUuid },
  });
  const [extractionType, setExtractionType] = useState<ExtractionType>();
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>();
  const [extractorValue, setExtractorValue] = useState<string>("");

  useEffect(() => {
    if (isOpen && fieldExtractor) {
      setExtractionType(fieldExtractor.type);
      setFieldMapping(fieldExtractor.mapping);
      setExtractorValue(fieldExtractor.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const extractionTypeOptions = enumKeys(ExtractionType).map((key) => ({
    value: ExtractionType[key],
    label: ExtractionType[key],
  }));
  const fieldMappingOptions = enumKeys(FieldMapping).map((key) => ({
    value: FieldMapping[key],
    label: FieldMapping[key],
  }));
  const contactOptions = data?.contacts.map((contact) => ({
    value: contact.uuid,
    label: contact.displayName,
  }));

  return (
    <Modal
      contentLabel="Edit field extractor"
      style={{
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "600px",
          width: "800px",
        },
      }}
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onClose}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "black",
        }}
      >
        <h3>Select extractor type</h3>
        <Select
          required
          options={extractionTypeOptions}
          value={extractionTypeOptions.find(
            (option) => option.value === extractionType,
          )}
          styles={{
            control: (base: CSSObjectWithLabel) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
            menu: (base: CSSObjectWithLabel) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
          }}
          onChange={(
            option: SingleValue<{ label: string; value: ExtractionType }>,
          ) => {
            if (option) {
              setExtractionType(option.value);
            }
          }}
        />
        {extractionType !== ExtractionType.Noop &&
          extractionType !== ExtractionType.SelectAll &&
          extractionType !== ExtractionType.Contact && (
            <>
              <h3>Set value</h3>
              <input
                required
                value={extractorValue}
                onChange={(e) => {
                  setExtractorValue(e.target.value);
                }}
              />
            </>
          )}
        {extractionType === ExtractionType.Contact && (
          <>
            <h3>Select contact</h3>
            <Select
              required
              value={contactOptions?.find(
                (option) => option.value === extractorValue,
              )}
              styles={{
                control: (base: CSSObjectWithLabel) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                }),
                menu: (base: CSSObjectWithLabel) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                }),
              }}
              options={contactOptions}
              onChange={(
                option: SingleValue<{ label: string; value: string }>,
              ) => {
                if (option) {
                  setExtractorValue(option.value);
                }
              }}
            />
          </>
        )}
        <h3>Select field mapping</h3>
        <Select
          required
          value={fieldMappingOptions.find(
            (option) => option.value === fieldMapping,
          )}
          styles={{
            control: (base: CSSObjectWithLabel) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
            menu: (base: CSSObjectWithLabel) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
          }}
          options={fieldMappingOptions}
          onChange={(
            option: SingleValue<{ label: string; value: FieldMapping }>,
          ) => {
            if (option) {
              setFieldMapping(option.value);
            }
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <button
          style={{ marginRight: "10px", height: "30px" }}
          onClick={() => {
            if (extractionType == null || fieldMapping == null) {
              alert("All values are required");
            } else {
              handleSave({
                type: extractionType,
                value: extractorValue,
                mapping: fieldMapping,
              });
            }
          }}
        >
          Save changes
        </button>
        <button onClick={onClose}>Discard</button>
      </div>
    </Modal>
  );
};

export default EditFieldExtractorModal;
